/**
 * Office "New version available" banner (card 5gncDWpbA9KV).
 *
 * The office app never reloads itself onto a new deploy — that could wipe a
 * half-filled form (e.g. the PO wizard). Instead registerSW.ts flags the
 * update (see client/field/pwaUpdate.ts) and this banner asks the user to
 * reload when they're ready. Two cases:
 *   - 'active'  — the new service worker already took over; Reload just
 *                 reloads onto the new bundle.
 *   - 'waiting' — the new worker is held back because a /field/* tab is open
 *                 in this browser; Reload activates it, then reloads.
 *
 * Renders nothing until an update is ready. Not dismissible — it stays until
 * the user reloads, so a stale bundle can't be ignored indefinitely.
 *
 * Sits above the bottom edge (bottom-24) so it never covers a page's sticky
 * footer (e.g. the pricebook wizard's Back / Next bar and its reason text).
 */

import { Loader2, RefreshCw } from 'lucide-react';
import { useState, useSyncExternalStore } from 'react';

import { applyOfficeUpdate, getUpdateState, subscribeUpdateState } from '../../field/pwaUpdate.js';
import { Button } from '../ui/button.js';

export function OfficeUpdateBanner() {
  const state = useSyncExternalStore(subscribeUpdateState, getUpdateState, () => 'none' as const);
  const [reloading, setReloading] = useState(false);

  if (state === 'none') {
    return null;
  }

  const onReload = () => {
    if (reloading) {
      return;
    }
    setReloading(true);
    applyOfficeUpdate();
  };

  return (
    <div
      className="fixed bottom-24 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg border bg-primary px-4 py-2 text-primary-foreground shadow-lg"
      role="status"
    >
      <RefreshCw className="h-4 w-4 shrink-0" aria-hidden />
      <span className="text-sm font-medium">New version available</span>
      <Button size="sm" variant="secondary" onClick={onReload} disabled={reloading}>
        {reloading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : 'Reload'}
      </Button>
    </div>
  );
}

export default OfficeUpdateBanner;
